import React, { useState, useEffect, useRef, useLayoutEffect, Suspense, useMemo } from 'react';
import { motion, AnimatePresence, useInView, useAnimation, useScroll, useTransform, useSpring } from 'framer-motion';
import {
  Terminal,
  Rocket,
  ChevronRight,
  Menu,
  X,
  Mail,
  Code2,
  Award,
  Download,
  ExternalLink,
  Globe,
  Cpu,
  Clock,
  User,
  Layout,
  Layers,
  Sparkles,
  Target,
  Zap,
  CheckCircle2,
  Trophy,
  ArrowRight,
  Star,
  Quote,
  MapPin,
  Phone
} from 'lucide-react';
import { FaGithub as Github, FaLinkedin as Linkedin } from 'react-icons/fa';
import config from './config';
import useHaptic from './hooks/useHaptic';
import SectionSeparator from './Components/Common/SectionSeparator';

import SEOHelmet from './Components/Common/SEOHelmet';
import { SkillSkeleton, ProjectSkeleton, TestimonialSkeleton } from './Components/Loading/HomeSkeletons';
import { lazyWithRetry } from './utils/lazyWithRetry';

const Chatbot = lazyWithRetry(() => import('./Components/Chatbot/Chatbot'));
const FloatingMoon = lazyWithRetry(() => import('./Components/Common/FloatingMoon'));
const TestimonialStack = lazyWithRetry(() => import('./Components/Testimonials/TestimonialStack'));

// --- DATA ---
const stats = [
  { number: '3+', label: 'Years Experience', icon: <Terminal size={20} /> },
  { number: '15+', label: 'Projects Completed', icon: <Award size={20} /> },
  { number: '5+', label: 'Core Technologies', icon: <Cpu size={20} /> }
];

const achievements = [
  "Developed Accessriano, an AR-based e-commerce platform",
  "Built responsive websites for ASN Health-Care and Ninja Creativity",
  "Deployed multiple full-stack MERN applications with modern UI/UX",
  "Focus on performance optimization and scalable architecture"
];

// --- ANIMATION VARIANTS ---
const fadeInUp = {
  hidden: { opacity: 0, y: 40 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.8, ease: [0.16, 1, 0.3, 1] }
  }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.15,
      delayChildren: 0.2
    }
  }
};

const slideInRight = {
  hidden: { opacity: 0, x: 50 },
  visible: {
    opacity: 1,
    x: 0,
    transition: { duration: 0.8, ease: "easeOut" }
  }
};

// Helper to render testimonial stack
const TestimonialsWrapper = ({ testimonials }) => {
  const [StackComponent, setStackComponent] = useState(null);

  useEffect(() => {
    import('./Components/Testimonials/TestimonialStack').then(module => {
      setStackComponent(() => module.default);
    });
  }, []);

  if (!StackComponent) return <TestimonialSkeleton />;

  const Stack = StackComponent;

  return (
    <div className="flex flex-col md:flex-row items-center justify-between gap-16">
      <div className="flex-1 text-center md:text-left">
        <motion.div variants={fadeInUp} className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-red-600/10 border border-red-600/20 text-red-600 text-[10px] font-black uppercase tracking-[0.2em] mb-6">
          <Quote size={12} /> Testimonials
        </motion.div>
        <motion.h2 variants={fadeInUp} className="text-3xl sm:text-4xl md:text-6xl font-black mb-8 leading-tight">
          What My <br className="hidden md:block" /> <span className="text-red-600">Clients</span> Say
        </motion.h2>
        <motion.p variants={fadeInUp} className="text-slate-400 text-lg max-w-xl mb-8 leading-relaxed">
          I take pride in delivering exceptional results for every project. Here is some feedback from the visionaries I've collaborated with.
        </motion.p>
        <motion.div variants={fadeInUp} className="flex items-center gap-4 justify-center md:justify-start text-slate-500 font-bold text-xs uppercase tracking-widest">
          <div className="w-12 h-px bg-slate-800" />
          <span>Swipe left to shuffle</span>
        </motion.div>
      </div>
      <div className="flex-1 flex justify-center items-center py-10">
        <Stack testimonials={testimonials} />
      </div>
    </div>
  );
};

const Home = ({ name, title, description, imageSrc }) => {
  const haptic = useHaptic();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [profile, setProfile] = useState({
    homeTitle: '',
    homeDescription: '',
    homeImage: ''
  });
  const [about, setAbout] = useState({
    title: '',
    subtitle: '',
    description1: '',
    description2: '',
    achievements: [],
    competencies: []
  });
  const [skills, setSkills] = useState([]);
  const [projects, setProjects] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [certifications, setCertifications] = useState([]);
  const [clients, setClients] = useState([]);
  const [activeCategory, setActiveCategory] = useState('All');
  const [projectCategory, setProjectCategory] = useState('Featured Projects');
  const [loading, setLoading] = useState(true);
  const [selectedProject, setSelectedProject] = useState(null);
  const [formStatus, setFormStatus] = useState({ submitting: false, success: false, error: null });
  const [formData, setFormData] = useState({ name: '', email: '', message: '' });

  // Animation refs and controls
  const textRef = useRef(null);
  const aboutRef = useRef(null);
  const skillsRef = useRef(null);
  const projectsRef = useRef(null);
  const contactRef = useRef(null);
  const testimonialsRef = useRef(null);
  const certificationsRef = useRef(null);
  const clientsRef = useRef(null);

  // Scroll Progress
  const { scrollYProgress } = useScroll();
  const scaleX = useSpring(scrollYProgress, {
    stiffness: 100,
    damping: 30,
    restDelta: 0.001
  });

  const [scrolled, setScrolled] = useState(false);
  const [navActiveIndex, setNavActiveIndex] = useState(0);
  const [navReady, setNavReady] = useState(false);
  const navItemRefs = useRef([]);
  const limelightRef = useRef(null);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);

      // Simple section tracking
      const sections = ['home', 'projects', 'skills', 'contact'];
      const scrollPos = window.scrollY + 200;

      for (let i = sections.length - 1; i >= 0; i--) {
        const element = document.getElementById(sections[i]);
        if (element && scrollPos >= element.offsetTop) {
          setNavActiveIndex(i);
          break;
        }
      }
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useLayoutEffect(() => {
    const limelight = limelightRef.current;
    const activeItem = navItemRefs.current[navActiveIndex];
    
    if (limelight && activeItem) {
      const newLeft = activeItem.offsetLeft + activeItem.offsetWidth / 2 - limelight.offsetWidth / 2;
      limelight.style.left = `${newLeft}px`;

      if (!navReady) {
        setTimeout(() => setNavReady(true), 50);
      }
    }
  }, [navActiveIndex, navReady]);

  const textInView = useInView(textRef, { once: true, threshold: 0.3 });
  const aboutInView = useInView(aboutRef, { once: true, threshold: 0.2 });
  const skillsInView = useInView(skillsRef, { once: true, threshold: 0.1 });
  const projectsInView = useInView(projectsRef, { once: true, threshold: 0.1 });
  const contactInView = useInView(contactRef, { once: true, threshold: 0.1 });
  const testimonialsInView = useInView(testimonialsRef, { once: true, threshold: 0.1 });
  const certificationsInView = useInView(certificationsRef, { once: true, threshold: 0.1 });
  const clientsInView = useInView(clientsRef, { once: true, threshold: 0.1 });

  const textControls = useAnimation();
  const aboutControls = useAnimation();
  const skillsControls = useAnimation();
  const projectsControls = useAnimation();
  const contactControls = useAnimation();
  const testimonialsControls = useAnimation();
  const certificationsControls = useAnimation();
  const clientsControls = useAnimation();

  useEffect(() => {
    // 1. Load from cache immediately for instant UI
    const cachedData = localStorage.getItem('portfolio_cache');
    if (cachedData) {
      try {
        const data = JSON.parse(cachedData);
        if (data.skills) setSkills(data.skills);
        if (data.projects) setProjects(data.projects);
        if (data.testimonials) setTestimonials(data.testimonials);
        if (data.profile) setProfile(data.profile);
        if (data.about) setAbout(data.about);
        if (data.certs) setCertifications(data.certs);
        if (data.clients) setClients(data.clients);
        setLoading(false); // Hide initial loader if cache exists
      } catch (e) {
        console.warn("Failed to parse portfolio cache");
      }
    }

    const fetchData = async () => {
      const endpoints = [
        { key: 'skills', url: `${config.apiUrl}/skills`, setter: (d) => (d.success || Array.isArray(d)) && setSkills(Array.isArray(d.skills) ? d.skills : (Array.isArray(d) ? d : (Array.isArray(d.data) ? d.data : []))) },
        { key: 'projects', url: `${config.apiUrl}/projects`, setter: (d) => (d.success || Array.isArray(d)) && setProjects(Array.isArray(d.projects) ? d.projects : (Array.isArray(d) ? d : (Array.isArray(d.data) ? d.data : []))) },
        { key: 'testimonials', url: `${config.apiUrl}/testimonials`, setter: (d) => d.success && setTestimonials(d.testimonials.filter(t => t.isApproved) || []) },
        { key: 'profile', url: `${config.apiUrl}/profile`, setter: (d) => d.success && setProfile(d.profile) },
        { key: 'about', url: `${config.apiUrl}/about`, setter: (d) => d.success && setAbout(d.about) },
        { key: 'certs', url: `${config.apiUrl}/certifications`, setter: (d) => d.success && setCertifications(d.certifications.filter(c => c.isVisible !== false)) },
        { key: 'clients', url: `${config.apiUrl}/clients`, setter: (d) => d.success && setClients(d.data || d || []) }
      ];

      // Fetch individually so fast requests don't wait for slow ones
      const freshData = {};
      
      const fetchItem = async (item) => {
        try {
          const res = await fetch(item.url);
          const d = await res.json();
          item.setter(d);
          
          // Collect for cache
          if (item.key === 'skills') freshData.skills = Array.isArray(d.skills) ? d.skills : (Array.isArray(d) ? d : (Array.isArray(d.data) ? d.data : []));
          if (item.key === 'projects') freshData.projects = Array.isArray(d.projects) ? d.projects : (Array.isArray(d) ? d : (Array.isArray(d.data) ? d.data : []));
          if (item.key === 'testimonials') freshData.testimonials = d.testimonials.filter(t => t.isApproved) || [];
          if (item.key === 'profile') freshData.profile = d.profile;
          if (item.key === 'about') freshData.about = d.about;
          if (item.key === 'certs') freshData.certs = d.certifications.filter(c => c.isVisible !== false);
          if (item.key === 'clients') freshData.clients = d.data || d || [];
          
        } catch (err) {
          console.error(`Failed to fetch ${item.key}:`, err);
        }
      };

      await Promise.all(endpoints.map(fetchItem));
      
      // Update cache
      localStorage.setItem('portfolio_cache', JSON.stringify(freshData));
      setLoading(false);
    };

    fetchData();
  }, []);

  useEffect(() => {
    if (textInView) textControls.start("visible");
  }, [textInView, textControls]);

  useEffect(() => {
    if (aboutInView) aboutControls.start("visible");
  }, [aboutInView, aboutControls]);

  useEffect(() => {
    if (skillsInView) skillsControls.start("visible");
  }, [skillsInView, skillsControls]);

  useEffect(() => {
    if (projectsInView) projectsControls.start("visible");
  }, [projectsInView, projectsControls]);

  useEffect(() => {
    if (contactInView) contactControls.start("visible");
  }, [contactInView, contactControls]);

  useEffect(() => {
    if (testimonialsInView) testimonialsControls.start("visible");
  }, [testimonialsInView, testimonialsControls]);

  useEffect(() => {
    if (certificationsInView) certificationsControls.start("visible");
  }, [certificationsInView, certificationsControls]);

  useEffect(() => {
    if (clientsInView) clientsControls.start("visible");
  }, [clientsInView, clientsControls]);

  const scrollToSection = (targetId) => {
    haptic.medium();
    const target = document.getElementById(targetId);
    if (!target) return;
    const NAVBAR_OFFSET = 80;
    const top = target.getBoundingClientRect().top + window.pageYOffset - NAVBAR_OFFSET;
    window.scrollTo({ top, behavior: 'smooth' });
    setIsMenuOpen(false);
  };

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    haptic.medium();
    setFormStatus({ submitting: true, success: false, error: null });

    try {
      const res = await fetch(`${config.apiUrl}/contact/submit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });
      const data = await res.json();
      if (data.success) {
        setFormStatus({ submitting: false, success: true, error: null });
        setFormData({ name: '', email: '', message: '' });
        haptic.selection();
      } else {
        throw new Error(data.message || 'Submission failed');
      }
    } catch (err) {
      setFormStatus({ submitting: false, success: false, error: err.message });
    }
  };

  const handleDownloadResume = () => {
    haptic.notification();
    window.open(`${config.apiUrl}/resume/download-direct`, '_blank');
  };

  // Group skills
  const groupedSkills = useMemo(() => {
    return skills.reduce((acc, skill) => {
      const category = skill.category || 'Other';
      if (!acc[category]) acc[category] = [];
      acc[category].push(skill);
      return acc;
    }, {});
  }, [skills]);

  const categories = useMemo(() => Object.keys(groupedSkills), [groupedSkills]);

  useEffect(() => {
    if (categories.length > 0 && (!activeCategory || activeCategory === 'All' || !categories.includes(activeCategory))) {
      setActiveCategory(categories[0]);
    }
  }, [categories, activeCategory]);

  // Group/Filter Projects
  const projectCategories = [...new Set(projects.map(p => p.category).filter(Boolean))];
  const filteredProjects = projects.filter(p => 
    p.category?.trim().toLowerCase() === projectCategory?.trim().toLowerCase()
  );

  useEffect(() => {
    console.log('--- Project Filter Debug ---');
    console.log('Current Category:', projectCategory);
    console.log('Total Projects:', projects.length);
    console.log('Filtered Count:', filteredProjects.length);
    if (projects.length > 0) {
      console.log('Available Categories:', [...new Set(projects.map(p => p.category))]);
    }
  }, [projectCategory, projects, filteredProjects.length]);

  const getToolsArray = (tools) => {
    if (!tools) return [];
    if (Array.isArray(tools)) return tools.filter(Boolean);
    if (typeof tools === 'string') return tools.split(',').map(t => t.trim()).filter(Boolean);
    return [];
  };

  // Animation variants
  const slideInLeft = {
    hidden: { opacity: 0, x: -50 },
    visible: {
      opacity: 1,
      x: 0,
      transition: { duration: 0.8, ease: "easeOut" }
    }
  };



  return (
    <div className="min-h-screen bg-[#0a0a0a] text-slate-200 selection:bg-red-500/30 selection:text-red-200 font-sans">
      <SEOHelmet 
        title={`${name || 'Qadirdad Kazi'} | ${profile.homeTitle || 'MERN Stack Developer'}`}
        description={profile.homeDescription || ""}
        keywords="MERN Stack Developer, Full Stack Developer, React, Node.js, Web Development, Portfolio"
      />
      {/* Schema.org JSON-LD */}
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Person",
          "name": name || "Qadirdad Kazi",
          "jobTitle": profile.homeTitle || "Full Stack Developer",
          "description": profile.homeDescription,
          "url": window.location.origin,
          "sameAs": [
            "https://github.com/Qadirdad-Kazi",
            "https://www.linkedin.com/in/qadirdad-kazi/"
          ]
        })}
      </script>
      {/* Scroll Progress Bar */}
      <motion.div
        className="fixed top-0 left-0 right-0 h-1 bg-red-600 origin-left z-[60] shadow-[0_0_15px_rgba(220,38,38,0.8)]"
        style={{ scaleX }}
      />

      {/* Navigation */}
      <nav className={`fixed top-0 w-full z-50 transition-all duration-500 ${
        scrolled 
          ? 'bg-[#0a0a0a]/90 backdrop-blur-xl border-b border-white/5 py-4' 
          : 'bg-transparent py-6'
      }`}>
        <div className="max-w-7xl mx-auto px-6 flex items-center justify-between">
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            className="flex items-center gap-3 font-bold text-2xl tracking-tighter cursor-pointer group"
            onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
          >
            <div className="relative">
              <div className="absolute inset-0 bg-red-600 blur-lg opacity-40 group-hover:opacity-100 transition-opacity" />
              <div className="relative w-11 h-11 bg-red-600 rounded-xl flex items-center justify-center text-white shadow-2xl">
                <Terminal size={26} strokeWidth={2.5} />
              </div>
            </div>
            <span className="font-black">QADIRDAD<span className="text-red-600 text-glow">.</span></span>
          </motion.div>

          <motion.div 
            initial="hidden"
            animate="visible"
            variants={staggerContainer}
            className="hidden md:flex items-center gap-10"
          >
            {['Home', 'About', 'Skills', 'Projects', 'Reviews', 'Contact'].map((item) => (
              <motion.button
                key={item}
                variants={fadeInUp}
                onClick={() => scrollToSection(item === 'Reviews' ? 'testimonials' : item.toLowerCase())}
                className="text-xs font-black uppercase tracking-[0.2em] text-slate-400 hover:text-red-500 transition-all relative group"
              >
                {item}
                <span className="absolute -bottom-2 left-0 w-0 h-0.5 bg-red-600 transition-all group-hover:w-full shadow-[0_0_10px_rgba(220,38,38,0.8)]" />
              </motion.button>
            ))}
            <motion.button
              variants={fadeInUp}
              className="px-8 py-3 bg-white text-black rounded-full text-xs font-black uppercase tracking-widest hover:bg-red-600 hover:text-white transition-all transform hover:scale-105 active:scale-95 shadow-xl shadow-white/5 hover:shadow-red-600/20"
              onClick={() => scrollToSection('contact')}
            >
              Hire Me
            </motion.button>
          </motion.div>

          {/* Mobile Toggle */}
          <button className="md:hidden text-slate-400" onClick={() => setIsMenuOpen(!isMenuOpen)}>
            {isMenuOpen ? <X /> : <Menu />}
          </button>
        </div>

        {/* Mobile Menu Overlay */}
        <AnimatePresence>
          {isMenuOpen && (
            <motion.div
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="absolute top-20 left-0 w-full bg-[#0a0a0a] border-b border-slate-800 p-6 flex flex-col gap-6 md:hidden z-50"
            >
              {['Home', 'About', 'Skills', 'Projects', 'Reviews', 'Contact'].map((item) => (
                <button
                  key={item}
                  onClick={() => scrollToSection(item === 'Reviews' ? 'testimonials' : item.toLowerCase())}
                  className="text-xl font-bold text-slate-400 hover:text-white text-left"
                >
                  {item}
                </button>
              ))}
            </motion.div>
          )}
        </AnimatePresence>
      </nav>


        {/* Hero Section */}
        <section id="home" className="relative pt-24 pb-10 md:pt-32 md:pb-16 overflow-hidden">
        {/* Background Elements */}
        <div className="absolute inset-0 z-0 overflow-hidden pointer-events-none">
          <div className="absolute top-[10%] left-[5%] w-[40%] h-[40%] bg-red-600/5 blur-[150px] rounded-full animate-pulse" />
          <div className="absolute bottom-[10%] right-[5%] w-[40%] h-[40%] bg-red-900/5 blur-[150px] rounded-full animate-pulse" />
          
          <div className="absolute top-0 left-1/4 w-px h-full bg-gradient-to-b from-transparent via-red-600/20 to-transparent" />
          <div className="absolute top-0 left-2/4 w-px h-full bg-gradient-to-b from-transparent via-slate-800/50 to-transparent" />
          <div className="absolute top-0 left-3/4 w-px h-full bg-gradient-to-b from-transparent via-red-600/20 to-transparent" />
        </div>

        <div className="max-w-7xl mx-auto px-6 relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              ref={textRef}
              variants={slideInLeft}
              initial="hidden"
              animate={textControls}
            >
              <motion.span
                variants={fadeInUp}
                className="inline-flex items-center gap-3 px-5 py-2 rounded-full bg-red-600/10 text-red-500 text-[10px] font-black tracking-[0.3em] uppercase mb-8 border border-red-600/20 shadow-[0_0_20px_rgba(220,38,38,0.1)]"
              >
                <div className="w-2 h-2 bg-red-600 rounded-full animate-ping" /> Available for Projects
              </motion.span>

              <motion.h1
                variants={fadeInUp}
                className="text-5xl md:text-7xl lg:text-9xl font-black leading-[0.9] mb-8 tracking-tighter"
              >
                {name ? (
                  <>
                    <span className="block">{name.split(' ')[0]}</span>
                    <span className="text-red-600 text-glow block">{name.split(' ')[1]}</span>
                  </>
                ) : (
                  <>
                    <span className="block">QADIRDAD</span>
                    <span className="text-red-600 text-glow block">KAZI</span>
                  </>
                )}
                <div className="mt-4 flex items-center gap-4 text-slate-500 text-xl lg:text-3xl font-bold tracking-tight">
                  <div className="h-px w-8 md:w-12 bg-red-600" />
                  {profile.homeTitle || ""}
                </div>
              </motion.h1>

              <motion.p
                variants={fadeInUp}
                className="text-lg md:text-xl text-slate-400 max-w-lg mb-10 leading-relaxed font-medium"
              >
                {profile.homeDescription || ""}
              </motion.p>

              <motion.div variants={fadeInUp} className="flex flex-col sm:flex-row gap-4">
                <button
                  onClick={() => scrollToSection('projects')}
                  className="group px-8 md:px-10 py-4 md:py-5 bg-red-600 hover:bg-red-700 text-white rounded-2xl font-bold transition-all flex items-center justify-center gap-2 shadow-xl shadow-red-600/20"
                >
                  Explore Work <ChevronRight className="group-hover:translate-x-1 transition-transform" />
                </button>
                <button
                  onClick={() => scrollToSection('contact')}
                  className="px-8 md:px-10 py-4 md:py-5 bg-slate-800/50 hover:bg-slate-800 text-white rounded-2xl font-bold transition-all border border-slate-700 backdrop-blur-sm"
                >
                  Get in Touch
                </button>
              </motion.div>

              {/* Social Links */}
              <motion.div variants={fadeInUp} className="mt-16 flex items-center gap-8 text-slate-500">
                <a href="https://github.com/Qadirdad-Kazi" target="_blank" rel="noopener noreferrer" className="hover:text-red-500 transition-colors"><Github size={24} /></a>
                <a href="https://www.linkedin.com/in/qadirdad-kazi/" target="_blank" rel="noopener noreferrer" className="hover:text-red-500 transition-colors"><Linkedin size={24} /></a>
                <a href="#" className="hover:text-red-500 transition-colors"><Mail size={24} /></a>
                <div className="h-4 w-px bg-slate-800" />

              </motion.div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 1, ease: "easeOut" }}
              className="relative flex items-center justify-center"
            >
              {/* Animated Rings */}
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="w-[120%] aspect-square border border-red-600/10 rounded-full animate-[spin_20s_linear_infinite]" />
                <div className="w-[140%] aspect-square border border-slate-800/20 rounded-full animate-[spin_30s_linear_infinite_reverse]" />
              </div>

              <div className="relative w-full max-w-md lg:max-w-lg aspect-[4/5] bg-slate-900 rounded-[40px] md:rounded-[60px] border border-white/5 overflow-hidden group shadow-[0_50px_100px_-20px_rgba(0,0,0,0.5)]">
                <img
                  src={profile.homeImage || imageSrc || "https://res.cloudinary.com/dzekrrkep/image/upload/v1767061266/profile/hgoartmhnwhm0ml1ncjy.jpg"}
                  alt="Profile"
                  loading="lazy"
                  className="w-full h-full object-cover grayscale group-hover:grayscale-0 transition-all duration-1000 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0a] via-transparent to-transparent" />

                {/* Floating Badge */}
                <div className="absolute bottom-6 md:bottom-10 left-6 md:left-10 right-6 md:right-10">
                  <div className="bg-glass p-4 md:p-6 rounded-[24px] md:rounded-[32px] flex items-center gap-4 md:gap-5 shadow-2xl">
                    <div className="w-10 h-10 md:w-14 md:h-14 bg-red-600 rounded-xl md:rounded-2xl flex items-center justify-center text-white shadow-xl shadow-red-600/30">
                      <Code2 className="w-5 h-5 md:w-7 md:h-7" strokeWidth={2.5} />
                    </div>
                    <div>
                      <div className="text-[8px] md:text-[10px] text-slate-500 font-black uppercase tracking-[0.3em] mb-1">Top Rated</div>
                      <div className="text-base md:text-xl font-black tracking-tight text-white uppercase">MERN DEVELOPER</div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Decorative elements */}
              <div className="absolute -top-10 -right-10 w-32 h-32 bg-red-600/20 blur-[60px] rounded-full" />
              <div className="absolute -bottom-10 -left-10 w-32 h-32 bg-slate-800/30 blur-[60px] rounded-full" />
            </motion.div>
          </div>
        </div>
        <SectionSeparator />
      </section>


      {/* About Section */}
      <section id="about" className="py-24 relative overflow-hidden">
        <Suspense fallback={null}>
          <FloatingMoon />
        </Suspense>
        <div className="max-w-7xl mx-auto px-6 relative z-10">
          <motion.div
            ref={aboutRef}
            variants={staggerContainer}
            initial="hidden"
            animate={aboutControls}
            className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center"
          >
            {/* Left: Content */}
            <div>
              <motion.div variants={fadeInUp} className="mb-12">
                <h2 className="text-3xl md:text-4xl lg:text-5xl font-black mb-8">
                  {about.title ? (
                    <>
                      {about.title.split(about.subtitle)[0]}
                      <span className="text-red-600">{about.subtitle}</span>
                      {about.title.split(about.subtitle)[1]}
                    </>
                  ) : (
                    <>Crafting <span className="text-red-600">Digital</span> Excellence</>
                  )}
                </h2>
                <p className="text-base md:text-lg text-slate-400 leading-relaxed mb-6">
                  {about.description1}
                </p>
                <p className="text-base md:text-lg text-slate-400 leading-relaxed">
                  {about.description2}
                </p>
              </motion.div>

              {/* Achievements */}
              <motion.div variants={fadeInUp} className="space-y-4 mb-12">
                {(about.achievements && about.achievements.length > 0 ? about.achievements : achievements).map((item, i) => (
                  <div key={i} className="flex items-start gap-4 p-3 md:p-4 rounded-2xl bg-slate-900/50 border border-slate-800/50 hover:border-red-500/30 transition-colors group">
                    <div className="mt-1 w-5 h-5 md:w-6 md:h-6 rounded-full bg-red-600/10 text-red-600 flex items-center justify-center shrink-0 group-hover:bg-red-600 group-hover:text-white transition-all">
                      <ChevronRight className="w-3 h-3 md:w-4 md:h-4" />
                    </div>
                    <span className="text-slate-300 text-sm md:text-base font-medium">{item}</span>
                  </div>
                ))}
              </motion.div>

              {/* Stats Grid */}
              <motion.div variants={fadeInUp} className="grid grid-cols-2 sm:grid-cols-3 gap-4 md:gap-6">
                {stats.map((stat, i) => (
                  <div key={i} className="text-center p-4 md:p-6 rounded-3xl bg-slate-900 border border-slate-800">
                    <div className="text-red-600 mb-2 flex justify-center">{stat.icon}</div>
                    <div className="text-xl md:text-2xl font-black text-white">
                      {stat.label === 'Projects Completed' 
                        ? projects.length 
                        : stat.label === 'Core Technologies' 
                          ? skills.length 
                          : stat.number}
                    </div>
                    <div className="text-[8px] md:text-[10px] text-slate-500 uppercase font-black tracking-widest">{stat.label}</div>
                  </div>
                ))}
              </motion.div>
            </div>

            {/* Right: Visual/Experience */}
            <motion.div variants={slideInRight} className="relative">
              <div className="relative z-10 p-10 bg-slate-900 rounded-[50px] border border-slate-800 shadow-2xl overflow-hidden">
                <div className="absolute top-0 right-0 p-8 opacity-10">
                  <Globe size={120} className="text-red-600" />
                </div>

                <div className="relative z-20">
                  <div className="w-16 h-16 bg-red-600 rounded-2xl flex items-center justify-center text-white mb-8 shadow-xl shadow-red-600/40">
                    <Award size={32} />
                  </div>
                  <h3 className="text-2xl font-black mb-4 uppercase tracking-tight">Core Competencies</h3>
                  <div className="space-y-6">
                    {(about.competencies && about.competencies.length > 0 ? about.competencies : [
                      { title: "Full Stack Development", desc: "End-to-end MERN stack expertise", icon: <Terminal size={20} /> },
                      { title: "Database Design", desc: "Efficient MongoDB schema design", icon: <Cpu size={20} /> },
                      { title: "DevOps & Cloud", desc: "CI/CD and deployment strategies", icon: <Globe size={20} /> }
                    ]).map((skill, i) => (
                      <div key={i} className="flex gap-4">
                        <div className="text-red-600 mt-1">
                          {typeof skill.icon === 'string' ? <i className={`bx ${skill.icon} text-xl`}></i> : skill.icon}
                        </div>
                        <div>
                          <div className="font-bold text-slate-200">{skill.title}</div>
                          <div className="text-sm text-slate-500">{skill.description || skill.desc}</div>
                        </div>
                      </div>
                    ))}
                  </div>

                  <button 
                    onClick={handleDownloadResume}
                    className="mt-12 w-full py-4 bg-slate-800 hover:bg-slate-700 text-white rounded-2xl font-bold transition-all border border-slate-700 flex items-center justify-center gap-2 group"
                  >
                    Download Resume <Download size={20} className="group-hover:translate-y-0.5 transition-transform" />
                  </button>
                </div>
              </div>

              {/* Decorative elements */}
              <div className="absolute -top-10 -right-10 w-40 h-40 bg-red-600/20 blur-3xl rounded-full" />
              <div className="absolute -bottom-10 -left-10 w-40 h-40 bg-slate-600/20 blur-3xl rounded-full" />
            </motion.div>
          </motion.div>
        </div>
        <SectionSeparator />
      </section>

      {/* Skills Section */}
      <section id="skills" className="py-24 relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 relative z-10">
          <motion.div
            ref={skillsRef}
            variants={staggerContainer}
            initial="hidden"
            animate={skillsControls}
          >
            <div className="text-center mb-20">
              <motion.h2 variants={fadeInUp} className="text-3xl md:text-4xl lg:text-5xl font-black mb-6">
                Technical <span className="text-red-600">Arsenal</span>
              </motion.h2>
              <motion.p variants={fadeInUp} className="text-slate-400 text-base md:text-lg max-w-2xl mx-auto">
                A comprehensive collection of technologies I use to bring complex ideas to life.
              </motion.p>
            </div>

            {/* Category Filter */}
            <motion.div variants={fadeInUp} className="flex flex-wrap justify-center gap-3 mb-16">
              {categories.map((cat) => (
                <button
                  key={cat}
                  onClick={() => { haptic.selection(); setActiveCategory(cat); }}
                  className={`px-3 md:px-4 py-1.5 md:py-2 rounded-full text-[10px] md:text-xs font-bold transition-all border ${activeCategory === cat
                      ? 'bg-red-600 border-red-600 text-white shadow-lg shadow-red-600/20 scale-105'
                      : 'bg-slate-900 border-slate-800 text-slate-400 hover:border-slate-700 hover:text-slate-200'
                    }`}
                >
                  {cat}
                </button>
              ))}
            </motion.div>

            {/* Skills Grid */}
            <div className="min-h-[400px]">
              {loading ? (
                <SkillSkeleton />
              ) : (
                <div className="flex flex-wrap justify-center gap-4 md:gap-6">
                  <AnimatePresence mode="popLayout">
                    {skills
                      .filter(s => activeCategory === 'All' || s.category === activeCategory)
                      .map((skill, index) => (
                        <motion.div
                          layout
                          key={skill._id}
                          initial={{ opacity: 0, y: 20 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, scale: 0.9 }}
                          transition={{ duration: 0.4, delay: index * 0.05 }}
                          whileHover={{ y: -5, transition: { duration: 0.2 } }}
                          className="w-[calc(50%-8px)] md:w-[calc(33.333%-16px)] lg:w-[calc(25%-18px)] xl:w-[calc(16.666%-20px)] group relative p-3 md:p-4 bg-slate-900/50 border border-slate-800/50 rounded-2xl md:rounded-3xl hover:border-red-500/30 transition-all backdrop-blur-sm flex flex-col items-center text-center gap-2 md:gap-3"
                        >
                          {/* Glow Effect */}
                          <div className="absolute inset-0 bg-red-600/0 group-hover:bg-red-600/5 transition-all rounded-2xl md:rounded-3xl" />

                          <div className="relative w-10 h-10 md:w-14 md:h-14 flex items-center justify-center bg-[#0a0a0a] rounded-xl md:rounded-2xl border border-slate-800 group-hover:border-red-500/50 transition-all shadow-inner">
                            {skill.iconUrl ? (
                              <img 
                                src={skill.iconUrl} 
                                alt={skill.language} 
                                loading="lazy"
                                className="w-6 h-6 md:w-8 md:h-8 object-contain group-hover:scale-110 transition-transform" 
                              />
                            ) : skill.iconClass ? (
                              <div className="text-slate-400 group-hover:text-red-500 transition-colors">
                                <i className={`${skill.iconClass} text-2xl md:text-3xl group-hover:scale-110 transition-transform`}></i>
                              </div>
                            ) : null}
                          </div>
                          <div className="w-full flex flex-col items-center gap-2 mt-1">
                            <div className="text-xs md:text-sm font-bold text-slate-200 tracking-tight group-hover:text-white transition-colors">{skill.language}</div>

                            {skill.tools && skill.tools.length > 0 && (
                              <div className="flex flex-wrap gap-1 justify-center mt-1">
                                {getToolsArray(skill.tools).slice(0, 3).map((t, i) => (
                                  <span key={i} className="text-[8px] md:text-[9px] px-1.5 py-0.5 rounded bg-slate-800/50 text-slate-400 border border-slate-700/50 uppercase tracking-wider line-clamp-1">
                                    {t}
                                  </span>
                                ))}
                              </div>
                            )}
                          </div>
                        </motion.div>
                      ))}
                  </AnimatePresence>
                </div>
              )}
            </div>
          </motion.div>
        </div>
        <SectionSeparator />
      </section>

      {/* Projects Section */}
      <section id="projects" className="py-24 relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 relative z-10">
          <motion.div
            ref={projectsRef}
            variants={staggerContainer}
            initial="hidden"
            animate={projectsControls}
          >
            <div className="flex flex-col md:flex-row justify-between items-start md:items-end gap-8 mb-16 md:mb-20">
              <div>
                <motion.h2 variants={fadeInUp} className="text-3xl md:text-4xl lg:text-5xl font-black mb-6 whitespace-normal md:whitespace-nowrap">
                  Featured <span className="text-red-600">Creations</span>
                </motion.h2>
                <motion.p variants={fadeInUp} className="text-slate-400 text-base md:text-lg max-w-xl">
                  A selection of my best work, ranging from AR-powered e-commerce to scalable enterprise solutions.
                </motion.p>
              </div>

              {/* Project Filter */}
              <motion.div variants={fadeInUp} className="flex flex-wrap gap-2">
                {projectCategories.map((cat) => (
                  <button
                    key={cat}
                    onClick={() => { haptic.selection(); setProjectCategory(cat); }}
                    className={`px-4 py-1.5 rounded-xl text-[10px] md:text-xs font-black uppercase tracking-widest transition-all border ${projectCategory === cat
                        ? 'bg-red-600 border-red-600 text-white'
                        : 'bg-slate-900 border-slate-800 text-slate-500 hover:border-slate-700'
                      }`}
                  >
                    {cat}
                  </button>
                ))}
              </motion.div>
            </div>

            {/* Projects Bento Grid */}
            <div key={projectCategory} className="grid grid-cols-1 md:grid-cols-4 lg:grid-cols-6 gap-6 auto-rows-[320px] min-h-[400px]">
                {loading ? (
                  <div key="loader" className="md:col-span-4 lg:col-span-6">
                    <ProjectSkeleton />
                  </div>
                ) : filteredProjects.length > 0 ? (
                    filteredProjects.map((project, index) => (
                      <motion.div
                        key={project._id}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.4, delay: index * 0.05 }}
                        className={`group relative bg-slate-900 rounded-[24px] md:rounded-[32px] border border-white/5 overflow-hidden hover:border-red-500/50 transition-all flex flex-col 
                          ${index % 5 === 0 ? 'md:col-span-2 md:row-span-2' : index % 5 === 1 ? 'md:col-span-2' : 'md:col-span-1 md:col-span-2 lg:col-span-2'}`}
                      >
                        <div className="border-beam" />
                        <div className="absolute inset-0 z-0">
                          <img
                            src={project.image || "/default-project.jpg"}
                            alt={project.title}
                            loading="lazy"
                            className="w-full h-full object-cover grayscale opacity-50 group-hover:grayscale-0 group-hover:opacity-100 transition-all duration-700 group-hover:scale-105"
                          />
                          <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0a] via-[#0a0a0a]/80 to-[#0a0a0a]/20" />
                        </div>

                        <div className="relative z-10 p-6 mt-auto flex flex-col h-full justify-end">
                          <div className="flex justify-between items-start mb-4">
                            <span className="px-3 py-1 bg-red-600 text-white text-[9px] font-black uppercase tracking-widest rounded-lg">
                              {project.category || 'Project'}
                            </span>
                            <div className="flex gap-2">
                              {project.github && (
                                <a href={project.github} target="_blank" rel="noreferrer" className="w-8 h-8 bg-black/50 backdrop-blur-md rounded-full flex items-center justify-center text-white hover:bg-red-600 transition-colors">
                                  <Github size={14} />
                                </a>
                              )}
                              {project.live && (
                                <a href={project.live} target="_blank" rel="noreferrer" className="w-8 h-8 bg-black/50 backdrop-blur-md rounded-full flex items-center justify-center text-white hover:bg-red-600 transition-colors">
                                  <ExternalLink size={14} />
                                </a>
                              )}
                            </div>
                          </div>
                          
                          <h3 className="text-base md:text-lg lg:text-xl font-black mb-2 group-hover:text-red-500 transition-colors line-clamp-2 leading-tight py-1">{project.title}</h3>
                          <p className="text-slate-400 text-xs md:text-sm leading-relaxed mb-4 line-clamp-2">
                            {project.description}
                          </p>

                          <div className="flex flex-wrap gap-2">
                            {getToolsArray(project.technologies)?.slice(0, 2).map((tech, i) => (
                              <span key={i} className="text-[8px] md:text-[9px] font-black uppercase tracking-wider text-slate-400 bg-black/30 backdrop-blur-sm px-2 py-1 rounded-md border border-white/5">
                                {tech}
                              </span>
                            ))}
                          </div>

                          <button
                            onClick={() => setSelectedProject(project)}
                            className="mt-4 md:mt-6 flex items-center gap-2 text-[9px] md:text-[10px] font-black uppercase tracking-[0.2em] text-red-600 hover:text-red-500 transition-colors group/btn"
                          >
                            View Details <ChevronRight className="w-2.5 h-2.5 md:w-3 md:h-3 group-hover/btn:translate-x-1 transition-transform" />
                          </button>
                        </div>
                      </motion.div>
                    ))
                ) : (
                  <div className="md:col-span-4 lg:col-span-6 flex flex-col items-center justify-center py-20 text-center">
                    <div className="w-20 h-20 bg-slate-900 rounded-full flex items-center justify-center mb-6 border border-slate-800">
                      <Rocket className="text-slate-600" size={32} />
                    </div>
                    <h3 className="text-xl font-black mb-2">No projects found</h3>
                    <p className="text-slate-500 max-w-xs">There are no projects in this category yet. Check back later!</p>
                    <p className="text-red-500/30 text-[10px] mt-4 font-mono uppercase tracking-widest">
                      Debug: {projectCategory} (0 matches)
                    </p>
                  </div>
                )}
            </div>
          </motion.div>
        </div>
        <SectionSeparator />
      </section>

      {/* Project Detail Modal */}
      <AnimatePresence>
        {selectedProject && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[100] flex items-center justify-center p-4 md:p-8 backdrop-blur-2xl bg-black/90"
            onClick={() => setSelectedProject(null)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0, y: 30 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.95, opacity: 0, y: 30 }}
              className="bg-[#0f111a] w-full max-w-6xl h-full md:h-[90vh] rounded-none md:rounded-[40px] border-none md:border md:border-slate-800 overflow-hidden shadow-[0_0_50px_rgba(0,0,0,0.5)] flex flex-col md:flex-row relative"
              onClick={e => e.stopPropagation()}
            >
              {/* Close Button */}
              <button 
                onClick={() => setSelectedProject(null)} 
                className="absolute top-6 right-6 z-50 w-12 h-12 bg-slate-900/80 backdrop-blur-md border border-slate-800 rounded-2xl flex items-center justify-center text-slate-400 hover:text-white hover:bg-red-600 transition-all group"
              >
                <X size={24} className="group-hover:rotate-90 transition-transform duration-300" />
              </button>

              {/* Left Side: Visuals & Meta */}
              <div className="w-full md:w-[45%] h-[40vh] md:h-full flex flex-col border-r border-slate-800/50 shrink-0">
                <div className="relative h-[25vh] md:h-2/3 overflow-hidden group">
                  <img src={selectedProject.image} alt={selectedProject.title} loading="lazy" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0f111a] via-transparent to-transparent" />
                  
                  {/* Category Badge */}
                  <div className="absolute bottom-4 md:bottom-6 left-4 md:left-6 flex flex-col gap-1">
                    <span className="px-3 md:px-4 py-1 md:py-1.5 bg-red-600/90 backdrop-blur-md text-white text-[8px] md:text-[10px] font-black uppercase tracking-[0.2em] rounded-full inline-block">
                      {selectedProject.category}
                    </span>
                    <h2 className="text-2xl md:text-4xl font-black text-white leading-tight drop-shadow-2xl">
                      {selectedProject.title}
                    </h2>
                  </div>
                </div>

                {/* Meta Grid */}
                <div className="p-8 grid grid-cols-2 gap-6 bg-[#0f111a]">
                  {selectedProject.role && (
                    <div className="flex items-center gap-4 group">
                      <div className="w-12 h-12 rounded-2xl bg-slate-900 border border-slate-800 flex items-center justify-center text-red-500 group-hover:bg-red-500 group-hover:text-white transition-all">
                        <User size={20} />
                      </div>
                      <div>
                        <p className="text-[10px] font-black uppercase text-slate-500 tracking-widest">Role</p>
                        <p className="text-sm font-bold text-slate-300">{selectedProject.role}</p>
                      </div>
                    </div>
                  )}
                  
                  {selectedProject.duration && (
                    <div className="flex items-center gap-4 group">
                      <div className="w-12 h-12 rounded-2xl bg-slate-900 border border-slate-800 flex items-center justify-center text-red-500 group-hover:bg-red-500 group-hover:text-white transition-all">
                        <Clock size={20} />
                      </div>
                      <div>
                        <p className="text-[10px] font-black uppercase text-slate-500 tracking-widest">Duration</p>
                        <p className="text-sm font-bold text-slate-300">{selectedProject.duration}</p>
                      </div>
                    </div>
                  )}

                  {/* Buttons */}
                  <div className="col-span-2 flex gap-3 mt-4">
                    {selectedProject.live && (
                      <a href={selectedProject.live} target="_blank" rel="noreferrer" className="flex-1 py-4 bg-red-600 text-white rounded-2xl font-black text-xs uppercase tracking-widest flex items-center justify-center gap-2 hover:bg-red-700 hover:-translate-y-1 transition-all shadow-lg shadow-red-600/20">
                        Launch Project <ExternalLink size={16} />
                      </a>
                    )}
                    {selectedProject.github && (
                      <a href={selectedProject.github} target="_blank" rel="noreferrer" className="w-16 h-14 bg-slate-900 text-white rounded-2xl flex items-center justify-center hover:bg-slate-800 transition-all border border-slate-800 hover:-translate-y-1">
                        <Github size={24} />
                      </a>
                    )}
                  </div>
                </div>
              </div>

              {/* Right Side: Details */}
              <div className="w-full md:w-[55%] h-full p-8 md:p-12 overflow-y-auto scrollbar-hide bg-[#0f111a]">
                <div className="space-y-12 pb-12">
                  {/* Overview */}
                  <section>
                    <div className="flex items-center gap-3 mb-6">
                      <Sparkles className="text-red-500" size={20} />
                      <h4 className="text-xs font-black uppercase tracking-[0.2em] text-slate-500">The Overview</h4>
                    </div>
                    <p className="text-slate-300 leading-relaxed text-lg font-medium italic border-l-4 border-red-500/30 pl-6">
                      "{selectedProject.overview || selectedProject.description}"
                    </p>
                  </section>

                  {/* Case Study Grid */}
                  <div className="grid grid-cols-1 gap-8">
                    {selectedProject.problem && (
                      <div className="p-6 bg-slate-900/40 rounded-3xl border border-slate-800/50">
                        <div className="flex items-center gap-3 mb-4 text-red-500">
                          <Target size={18} />
                          <h5 className="text-[11px] font-black uppercase tracking-widest">The Challenge</h5>
                        </div>
                        <p className="text-slate-400 text-sm leading-relaxed">{selectedProject.problem}</p>
                      </div>
                    )}

                    {selectedProject.solution && (
                      <div className="p-6 bg-slate-900/40 rounded-3xl border border-slate-800/50">
                        <div className="flex items-center gap-3 mb-4 text-emerald-500">
                          <Zap size={18} />
                          <h5 className="text-[11px] font-black uppercase tracking-widest">The Solution</h5>
                        </div>
                        <p className="text-slate-400 text-sm leading-relaxed">{selectedProject.solution}</p>
                      </div>
                    )}

                    {selectedProject.outcome && (
                      <div className="p-6 bg-red-600/5 rounded-3xl border border-red-600/20">
                        <div className="flex items-center gap-3 mb-4 text-red-500">
                          <Trophy size={18} />
                          <h5 className="text-[11px] font-black uppercase tracking-widest">The Result</h5>
                        </div>
                        <p className="text-slate-400 text-sm leading-relaxed">{selectedProject.outcome}</p>
                      </div>
                    )}
                  </div>

                  {/* Key Features */}
                  {selectedProject.keyFeatures && selectedProject.keyFeatures.length > 0 && (
                    <section>
                      <div className="flex items-center gap-3 mb-6">
                        <Layout className="text-red-500" size={20} />
                        <h4 className="text-xs font-black uppercase tracking-[0.2em] text-slate-500">Key Features</h4>
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        {selectedProject.keyFeatures.map((feature, i) => (
                          <div key={i} className="flex items-center gap-3 p-4 bg-slate-900 rounded-2xl border border-slate-800 group hover:border-red-500/30 transition-all">
                            <CheckCircle2 size={16} className="text-red-600 shrink-0" />
                            <span className="text-slate-300 text-xs font-bold">{feature}</span>
                          </div>
                        ))}
                      </div>
                    </section>
                  )}

                  {/* Tech Stack */}
                  <section>
                    <div className="flex items-center gap-3 mb-6">
                      <Layers className="text-red-500" size={20} />
                      <h4 className="text-xs font-black uppercase tracking-[0.2em] text-slate-500">Tech Stack</h4>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {getToolsArray(selectedProject.technologies)?.map((tech, i) => (
                        <span key={i} className="px-4 py-2 bg-[#0a0a0a] text-slate-300 text-[10px] font-black uppercase tracking-widest rounded-xl border border-slate-800 hover:border-red-500/50 hover:text-red-500 transition-all cursor-default">
                          {tech}
                        </span>
                      ))}
                    </div>
                  </section>

                  {/* Interactive Demo Embed */}
                  {selectedProject.demoEmbed && (
                    <section>
                      <div className="flex items-center gap-3 mb-6">
                        <Rocket className="text-red-500" size={20} />
                        <h4 className="text-xs font-black uppercase tracking-[0.2em] text-slate-500">Live Demo</h4>
                      </div>
                      <div className="rounded-[32px] overflow-hidden border border-slate-800 bg-black aspect-video" dangerouslySetInnerHTML={{ __html: selectedProject.demoEmbed }} />
                    </section>
                  )}
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Clients/Partners Section */}
      {about.showClients !== false && clients.length > 0 && (
        <section id="clients" className="py-20 relative overflow-hidden bg-[#0d0d0d]/50">
          <div className="max-w-7xl mx-auto px-6 relative z-10">
            <motion.div
              ref={clientsRef}
              variants={staggerContainer}
              initial="hidden"
              animate={clientsControls}
            >
              <div className="text-center mb-12">
                <motion.h4 variants={fadeInUp} className="text-[10px] font-black uppercase tracking-[0.3em] text-red-600 mb-3">
                  Collaboration Network
                </motion.h4>
                <motion.h2 variants={fadeInUp} className="text-3xl font-black text-white">
                  Trusted By <span className="text-slate-500">Industry Leaders</span>
                </motion.h2>
              </div>

              {/* Infinite Logo Marquee */}
              <div className="relative flex overflow-hidden group">
                <div className="flex gap-12 py-4 animate-marquee whitespace-nowrap">
                  {[...clients, ...clients].map((client, i) => (
                    <motion.div
                      key={`${client._id}-${i}`}
                      variants={fadeInUp}
                      className="flex items-center gap-4 px-8 py-4 rounded-2xl bg-slate-900/40 border border-slate-800/50 backdrop-blur-sm grayscale opacity-50 hover:grayscale-0 hover:opacity-100 hover:border-red-500/30 transition-all duration-500 group/client"
                    >
                      <div className="w-10 h-10 rounded-xl bg-[#0a0a0a] flex items-center justify-center overflow-hidden border border-slate-800">
                        {client.website?.image ? (
                          <img src={client.website.image} alt={client.name} loading="lazy" className="w-full h-full object-cover" />
                        ) : (
                          <span className="text-lg font-black text-red-500">{client.name.charAt(0)}</span>
                        )}
                      </div>
                      <div className="flex flex-col">
                        <span className="text-sm font-black text-white tracking-tight">{client.name}</span>
                        <span className="text-[9px] font-bold text-slate-500 uppercase tracking-widest">{client.company || 'Enterprise'}</span>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </div>
            </motion.div>
          </div>
          
          {/* Subtle noise/gradient overlays for fade effect */}
          <div className="absolute top-0 left-0 w-32 h-full bg-gradient-to-r from-[#0a0a0a] to-transparent z-20 pointer-events-none" />
          <div className="absolute top-0 right-0 w-32 h-full bg-gradient-to-l from-[#0a0a0a] to-transparent z-20 pointer-events-none" />
        </section>
      )}

      {/* Testimonials Section */}
      <section id="testimonials" className="py-24 relative overflow-hidden bg-slate-900/20">
        <div className="max-w-7xl mx-auto px-6 relative z-10">
          <motion.div
            ref={testimonialsRef}
            variants={staggerContainer}
            initial="hidden"
            animate={testimonialsControls}
          >
            <div className="min-h-[400px] mt-10">
              {loading ? (
                <TestimonialSkeleton />
              ) : (
                <TestimonialsWrapper testimonials={testimonials} />
              )}
            </div>
          </motion.div>
        </div>
        <SectionSeparator />
      </section>

      {/* Certifications Section */}
      {about.showCertifications !== false && certifications.length > 0 && (
        <section id="certifications" className="py-24 relative overflow-hidden">
          <div className="max-w-7xl mx-auto px-6 relative z-10">
            <motion.div
              ref={certificationsRef}
              variants={staggerContainer}
              initial="hidden"
              animate={certificationsControls}
            >
              <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-16">
                <div>
                  <motion.h2 variants={fadeInUp} className="text-3xl md:text-4xl lg:text-5xl font-black mb-6">
                    Professional <span className="text-red-600">Vault</span>
                  </motion.h2>
                  <motion.p variants={fadeInUp} className="text-slate-400 text-base md:text-lg max-w-xl">
                    A collection of verified credentials and professional certifications validating my technical expertise.
                  </motion.p>
                </div>
                <motion.div variants={fadeInUp} className="hidden md:block">
                  <div className="flex items-center gap-4 text-[10px] font-black uppercase tracking-[0.2em] text-slate-500">
                    <div className="w-12 h-[1px] bg-red-600/30"></div>
                    Verified Credentials
                  </div>
                </motion.div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {certifications.map((cert, index) => (
                  <motion.div
                    key={cert._id}
                    variants={fadeInUp}
                    className="group relative"
                  >
                    <div className="absolute -inset-0.5 bg-gradient-to-br from-red-600/20 to-slate-800/20 rounded-[24px] md:rounded-[32px] blur opacity-0 group-hover:opacity-100 transition duration-500" />
                    <div className="relative h-full p-6 md:p-8 rounded-[24px] md:rounded-[32px] bg-slate-900/40 backdrop-blur-xl border border-slate-800 hover:border-red-500/30 transition-all duration-500 flex flex-col gap-6">
                      <div className="relative h-48 rounded-2xl bg-[#0a0a0a] overflow-hidden group/img">
                        {cert.fileType === 'pdf' ? (
                          <div className="w-full h-full flex flex-col items-center justify-center bg-slate-800/30">
                            <i className="bx bxs-file-pdf text-5xl text-red-600 mb-2"></i>
                            <span className="text-[10px] font-black uppercase tracking-widest text-slate-500">Digital Artifact</span>
                          </div>
                        ) : (
                          <img 
                            src={cert.fileUrl} 
                            alt={cert.title} 
                            className="w-full h-full object-cover grayscale group-hover/img:grayscale-0 group-hover/img:scale-110 transition-all duration-700"
                          />
                        )}
                        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent opacity-0 group-hover/img:opacity-100 transition-opacity flex items-end p-6">
                          <a 
                            href={cert.fileUrl} 
                            target="_blank" 
                            rel="noopener noreferrer" 
                            className="px-4 py-2 bg-red-600 text-white text-[10px] font-black uppercase rounded-lg tracking-widest flex items-center gap-2 hover:bg-red-700 transition-colors"
                          >
                            View Document <i className="bx bx-link-external text-sm"></i>
                          </a>
                        </div>
                      </div>

                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-3">
                          <div className="w-2 h-2 rounded-full bg-red-600 shadow-[0_0_8px_rgba(220,38,38,0.4)]"></div>
                          <span className="text-[10px] font-black uppercase tracking-widest text-slate-500">
                            {cert.organization}
                          </span>
                        </div>
                        <h3 className="text-xl font-black text-white group-hover:text-red-500 transition-colors line-clamp-1 mb-2">
                          {cert.title}
                        </h3>
                        <p className="text-sm text-slate-400 line-clamp-2 leading-relaxed">
                          {cert.description}
                        </p>
                      </div>

                      <div className="pt-6 border-t border-slate-800 flex items-center justify-between">
                        <div className="text-[10px] font-bold text-slate-500 uppercase tracking-widest">
                          {new Date(cert.issueDate).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
                        </div>
                        <div className="w-8 h-8 rounded-full bg-slate-800 flex items-center justify-center text-red-500 opacity-0 group-hover:opacity-100 transition-opacity">
                          <i className="bx bx-chevron-right text-xl"></i>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </div>
          <SectionSeparator />
        </section>
      )}

      {/* Clients Marquee Section (Optional: added animations here too) */}
      <motion.section 
        ref={clientsRef}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
        variants={fadeInUp}
        className="py-16 border-t border-white/5 bg-black/20"
      >
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex flex-col items-center gap-8">

            <div className="w-full overflow-hidden relative">
              <div className="flex items-center gap-12 animate-marquee whitespace-nowrap">
                {[...clients, ...clients].map((client, i) => (
                  <div key={i} className="flex items-center gap-3 grayscale opacity-30 hover:grayscale-0 hover:opacity-100 transition-all cursor-pointer">
                    <div className="w-8 h-8 rounded-lg bg-red-600/10 flex items-center justify-center text-red-500 font-black text-xs">
                      {client.name?.[0] || 'C'}
                    </div>
                    <span className="text-sm font-bold text-slate-400 uppercase tracking-tighter">{client.name || client.company}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </motion.section>

      {/* Contact Section */}


      {/* Contact Section */}
      <section id="contact" className="py-24 relative overflow-hidden bg-gradient-to-b from-[#0a0a0a] to-[#0d0d0d]">
        <div className="max-w-7xl mx-auto px-6 relative z-10">
          <motion.div
            ref={contactRef}
            variants={staggerContainer}
            initial="hidden"
            animate={contactControls}
            className="grid lg:grid-cols-2 gap-12 lg:gap-20"
          >
            {/* Column 1: Info & Resume */}
            <div className="space-y-12">
              <div>
                <motion.h2 variants={fadeInUp} className="text-3xl md:text-4xl lg:text-5xl font-black mb-8">
                  Let's <span className="text-red-600">Connect</span>
                </motion.h2>
                <motion.p variants={fadeInUp} className="text-slate-400 text-lg mb-12 leading-relaxed">
                  I'm always open to discussing new projects, creative ideas or opportunities to be part of your vision.
                </motion.p>

                <motion.div variants={fadeInUp} className="space-y-8">
                  <div className="flex items-center gap-6 group">
                    <div className="w-14 h-14 bg-slate-900 rounded-2xl flex items-center justify-center text-red-600 border border-slate-800 group-hover:border-red-500/50 transition-all">
                      <Mail size={24} />
                    </div>
                    <div>
                      <div className="text-[10px] text-slate-500 font-black uppercase tracking-widest mb-1">Email Me</div>
                      <div className="text-lg font-bold text-slate-200">qadirdadkazi@gmail.com</div>
                    </div>
                  </div>

                  <div className="flex items-center gap-6 group">
                    <div className="w-14 h-14 bg-slate-900 rounded-2xl flex items-center justify-center text-red-600 border border-slate-800 group-hover:border-red-500/50 transition-all">
                      <Globe size={24} />
                    </div>
                    <div>
                      <div className="text-[10px] text-slate-500 font-black uppercase tracking-widest mb-1">Based In</div>
                      <div className="text-lg font-bold text-slate-200">Karachi, Pakistan</div>
                    </div>
                  </div>
                </motion.div>
              </div>

              {/* Resume Download */}
              <motion.div variants={fadeInUp} className="p-6 md:p-8 bg-slate-900/50 rounded-3xl border border-slate-800 backdrop-blur-sm relative overflow-hidden">
                <div className="relative z-10">
                  <h3 className="text-lg md:text-xl font-black mb-4">Want to see more?</h3>
                  <p className="text-slate-400 text-xs md:text-sm mb-6">Download my full resume to get a detailed view of my experience and certifications.</p>
                  <button 
                    onClick={handleDownloadResume}
                    className="w-full sm:w-auto px-6 py-3 bg-white text-black rounded-xl font-bold flex items-center justify-center gap-2 hover:bg-slate-200 transition-all"
                  >
                    Download Resume <Download size={18} />
                  </button>
                </div>
                <div className="absolute top-0 right-0 p-4 opacity-5">
                  <Award size={100} />
                </div>
              </motion.div>
            </div>

            {/* Column 2: Contact Form */}
            <motion.div variants={fadeInUp} className="relative">
              <form onSubmit={handleContactSubmit} className="p-6 md:p-10 bg-slate-900 rounded-[32px] md:rounded-[40px] border border-slate-800 shadow-2xl space-y-6">
                <div className="grid md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <label className="text-xs font-black uppercase tracking-widest text-slate-500">Full Name</label>
                    <input
                      required
                      type="text"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="w-full px-6 py-4 bg-[#0a0a0a] rounded-2xl border border-slate-800 focus:border-red-600 outline-none transition-all text-white font-medium"
                      placeholder="John Doe"
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-xs font-black uppercase tracking-widest text-slate-500">Email Address</label>
                    <input
                      required
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      className="w-full px-6 py-4 bg-[#0a0a0a] rounded-2xl border border-slate-800 focus:border-red-600 outline-none transition-all text-white font-medium"
                      placeholder="john@example.com"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <label className="text-xs font-black uppercase tracking-widest text-slate-500">Your Message</label>
                  <textarea
                    required
                    rows="5"
                    value={formData.message}
                    onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                    className="w-full px-6 py-4 bg-[#0a0a0a] rounded-2xl border border-slate-800 focus:border-red-600 outline-none transition-all text-white font-medium resize-none"
                    placeholder="Tell me about your project..."
                  />
                </div>

                {formStatus.error && <p className="text-red-500 text-xs font-bold">{formStatus.error}</p>}
                {formStatus.success && <p className="text-green-500 text-xs font-bold">Message sent successfully!</p>}

                <button
                  disabled={formStatus.submitting}
                  className="w-full py-5 bg-red-600 hover:bg-red-700 text-white rounded-2xl font-black uppercase tracking-[0.2em] transition-all flex items-center justify-center gap-2 shadow-xl shadow-red-600/20 disabled:opacity-50"
                >
                  {formStatus.submitting ? 'Sending...' : 'Send Message'} <Rocket size={20} />
                </button>
              </form>
            </motion.div>
          </motion.div>
        </div>
      </section>
      
      {/* Enhanced 4-Column Footer */}
      <footer className="relative bg-slate-900/20 mt-16 w-full border-t border-white/5 rounded-t-[40px] overflow-hidden">
        {/* Decorative background glow */}
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-1/2 h-px bg-gradient-to-r from-transparent via-red-600/50 to-transparent" />
        
        <div className="mx-auto max-w-7xl px-6 pt-16 pb-12">
          <div className="grid grid-cols-1 gap-12 lg:grid-cols-3">
            {/* Column 1: Branding */}
            <div>
              <div className="flex items-center gap-3 justify-center sm:justify-start">
                <div className="w-10 h-10 rounded-xl bg-red-600 flex items-center justify-center text-white shadow-lg shadow-red-600/20">
                  <Terminal size={24} />
                </div>
                <span className="text-2xl font-black tracking-tighter text-white">
                  QADIRDAD <span className="text-red-600">KAZI</span>
                </span>
              </div>

              <p className="text-slate-400 mt-6 max-w-md text-center leading-relaxed sm:max-w-xs sm:text-left text-sm font-medium">
                Building high-performance digital experiences with the MERN stack. Specialized in scalable architecture and premium UI/UX design.
              </p>

              <div className="mt-8 flex justify-center gap-6 sm:justify-start">
                {[
                  { icon: <Github size={20} />, href: "https://github.com/Qadirdad-Kazi" },
                  { icon: <Linkedin size={20} />, href: "https://www.linkedin.com/in/qadirdad-kazi/" }
                ].map((social, i) => (
                  <a
                    key={i}
                    href={social.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-10 h-10 rounded-full bg-slate-900 border border-slate-800 flex items-center justify-center text-slate-400 hover:text-red-600 hover:border-red-600/50 transition-all duration-300"
                  >
                    {social.icon}
                  </a>
                ))}
              </div>
            </div>

            {/* Column 2 & 3: Links */}
            <div className="grid grid-cols-2 gap-8 md:grid-cols-3 lg:col-span-2">
              <div className="text-center sm:text-left">
                <p className="text-xs font-black uppercase tracking-[0.2em] text-slate-500 mb-8">Navigation</p>
                <ul className="space-y-4">
                  {[
                    { text: 'Portfolio', id: 'projects' },
                    { text: 'Experience', id: 'about' },
                    { text: 'Skills', id: 'skills' },
                    { text: 'Testimonials', id: 'testimonials' }
                  ].map((link) => (
                    <li key={link.text}>
                      <button
                        onClick={() => scrollToSection(link.id)}
                        className="text-slate-400 hover:text-red-600 transition-colors text-sm font-bold"
                      >
                        {link.text}
                      </button>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="text-center sm:text-left">
                <p className="text-xs font-black uppercase tracking-[0.2em] text-slate-500 mb-8">Services</p>
                <ul className="space-y-4">
                  {[
                    'Web Development',
                    'Deployment',
                    'Mobile Apps',
                    'Cloud Solutions'
                  ].map((service) => (
                    <li key={service}>
                      <span className="text-slate-400 text-sm font-bold">{service}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="text-center sm:text-left col-span-2 md:col-span-1">
                <p className="text-xs font-black uppercase tracking-[0.2em] text-slate-500 mb-8">Contact</p>
                <ul className="space-y-6">
                  {[
                    { icon: <Mail size={16} />, text: 'qadirdadkazi@gmail.com' },
                    { icon: <Phone size={16} />, text: '+923106029167' },
                    { icon: <MapPin size={16} />, text: 'Karachi, Pakistan' }
                  ].map((item, i) => (
                    <li key={i} className="flex items-center justify-center sm:justify-start gap-3">
                      <div className="text-red-600">{item.icon}</div>
                      <span className="text-slate-400 text-sm font-bold">{item.text}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>

          <div className="mt-16 border-t border-white/5 pt-8">
            <div className="flex flex-col md:flex-row justify-between items-center gap-6">
              <p className="text-xs font-black uppercase tracking-widest text-slate-600">
                &copy; {new Date().getFullYear()} QADIRDAD KAZI. ALL RIGHTS RESERVED.
              </p>
              <div className="flex items-center gap-6">
                <span className="text-[10px] font-black uppercase tracking-widest text-slate-700">Built with MERN & GSAP</span>
                <div className="w-2 h-2 rounded-full bg-red-600 animate-pulse" />
              </div>
            </div>
          </div>
        </div>
      </footer>
      {/* Floating Minimal Navigation with Limelight Effect */}
      <motion.div 
        initial={{ y: 100, x: '-50%' }}
        animate={{ y: -40, x: '-50%' }}
        className="fixed bottom-0 left-1/2 z-[100] px-4 md:px-6 py-3 md:py-4 bg-black/40 backdrop-blur-2xl rounded-full border border-white/10 shadow-2xl flex items-center gap-1 md:gap-4 scale-[0.85] sm:scale-100"
      >
        {/* Limelight Background Beam */}
        <div 
          ref={limelightRef}
          className={`absolute top-0 z-10 w-12 h-[2px] bg-red-600 shadow-[0_20px_40px_rgba(220,38,38,0.8)] ${
            navReady ? 'transition-[left] duration-500 ease-[cubic-bezier(0.23,1,0.32,1)]' : ''
          }`}
          style={{ left: '-999px' }}
        >
          <div className="absolute left-[-50%] top-[2px] w-[200%] h-16 [clip-path:polygon(10%_100%,35%_0,65%_0,90%_100%)] bg-gradient-to-b from-red-600/20 to-transparent pointer-events-none" />
        </div>

        {[
          { icon: <Terminal size={18} />, label: 'Home', id: 'home' },
          { icon: <Rocket size={18} />, label: 'Projects', id: 'projects' },
          { icon: <Code2 size={18} />, label: 'Skills', id: 'skills' },
          { icon: <Mail size={18} />, label: 'Contact', id: 'contact' }
        ].map((item, index) => (
          <button
            key={item.label}
            ref={el => (navItemRefs.current[index] = el)}
            onClick={() => {
              scrollToSection(item.id);
              setNavActiveIndex(index);
            }}
            className={`w-12 h-12 flex items-center justify-center transition-all duration-300 rounded-full group relative z-20 ${
              navActiveIndex === index ? 'text-red-600' : 'text-slate-400 hover:text-slate-200'
            }`}
          >
            <div className={`transition-transform duration-300 ${navActiveIndex === index ? 'scale-110' : 'scale-100'}`}>
              {item.icon}
            </div>
            <span className="absolute -top-10 left-1/2 -translate-x-1/2 px-3 py-1 bg-slate-900 border border-slate-800 text-white text-[10px] font-black uppercase tracking-widest rounded-md opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap">
              {item.label}
            </span>
          </button>
        ))}
        <div className="w-px h-6 bg-white/10 mx-2 z-20" />
        <a href="https://www.linkedin.com/in/qadirdad-kazi/" target="_blank" rel="noopener noreferrer" className="w-12 h-12 flex items-center justify-center text-slate-400 hover:text-red-600 transition-all z-20">
          <Linkedin size={18} />
        </a>
      </motion.div>
      <Suspense fallback={null}>
        <Chatbot context={['home', 'projects', 'skills', 'contact'][navActiveIndex]} />
      </Suspense>
    </div>
  );
};

export default Home;
